/**
 * Recharts Stacked Bar implementation. Multi-series stacked vertically.
 * Colors come from chartTheme('recharts').theme.categoricalPalette.
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { StackedBarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = StackedBarChartProps & CommonChartProps;

export function RechartsStackedBar({ data, xKey, seriesKeys, xLabel, yLabel }: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.axis.stroke} />
        <XAxis dataKey={xKey} tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} />
        <YAxis tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
        <Tooltip
          content={({ active, payload, label }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                header={String(label ?? '')}
                rows={payload.map((p, i) => ({
                  color: (p.color as string) ?? palette[i % palette.length],
                  label: String(p.name ?? p.dataKey ?? ''),
                  value: (p.value as number) ?? 0,
                }))}
              />
            ) : null
          }
        />
        {seriesKeys.length > 1 && (
          <Legend wrapperStyle={{ fontFamily: theme.legend.fontFamily, fontSize: theme.legend.fontSize }} />
        )}
        {seriesKeys.map((key, i) => (
          <Bar key={key} dataKey={key} stackId="stack" fill={palette[i % palette.length]} radius={i === seriesKeys.length - 1 ? [4, 4, 0, 0] : 0} />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
